import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { marche as api } from '../services/api';
import { nb, pct1, moisAn, usePanneau, MOIS_DISPO, basculer } from './ventesUtils';
import './Ventes.css';
import './Stock.css';
import './Marche.css';
import MarcheAnalyseIa from './MarcheAnalyseIa';
import { useBi } from '../BiContext';

// « 12 mois glissants » retiré le 2026-10-12, comme dans ventesUtils.
export const PERIODES = [{ code: 'ytd', label: 'Cumul année (YTD)' }];
export const VPVU = [{ code: '', label: 'VP + VUL' }, { code: 'VP', label: 'VP' }, { code: 'VUL', label: 'VUL' }];
// « 2026-10-19 » → « lundi 19 octobre 2026 » : la date d'arrêté du marché.
export const dateArrete = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const signePt = (v) => (v == null ? '—' : (v > 0 ? '+' : '') + pct1(v).replace(' %', ' pt'));
const signeNb = (v) => (v == null ? '—' : (v > 0 ? '+' : '') + nb(v));
export const capDep = (s) => (s === 'ST PIERRE ET MI.' ? 'St-Pierre' : s.charAt(0) + s.slice(1).toLowerCase());
const cls = (v) => (v == null || v === 0 ? '' : v > 0 ? ' tbl-pos' : ' tbl-neg');
// Colonne « vs N-1 » : classes de Ventes.css (td.vt-d.pos / .neg).
const clsD = (v) => (v == null || v === 0 ? '' : v > 0 ? ' pos' : ' neg');

// ─── Mise en page du rapport Marché du CRM, vocabulaire visuel du BI ─────────
// Mêmes blocs que le CRM (Reporting financier ▸ Marché), dans le même ordre :
// KPI, vue par département, parts de marché (6 vues à dépliage), évolution
// mensuelle (6 dimensions, parts ou volumes), nos marques × département.
// Écarts de forme, imposés par le canon BI (CLAUDE.md §4-5) : pas de barre dans
// les tableaux, pas de flèche ▲▼ — l'évolution N-1 a sa propre colonne, colorée
// par son signe ; le rang d'une ligne se lit « +2 », « =», « nouv. ».

const VUES_PARTS = [
  { c: 'marque', l: 'Par marque', col: 'Marque', cle: 'par_marque', enfants: ['modeles', 'clients'], rang: true },
  { c: 'modele', l: 'Par modèle', col: 'Modèle', cle: 'par_modele', enfants: ['clients'], rang: true },
  { c: 'groupe', l: 'Par groupe', col: 'Groupe', cle: 'par_groupe', enfants: ['marques', 'clients'], rang: true },
  { c: 'client', l: 'Par type de client', col: 'Type de client', cle: 'par_client', enfants: ['marques'], rang: false },
  { c: 'origine', l: 'Par origine', col: 'Origine', cle: 'par_origine', enfants: ['marques', 'clients'], rang: true },
  { c: 'constructeur', l: 'Par constructeur', col: 'Constructeur', cle: 'par_constructeur', enfants: ['marques', 'clients'], rang: true },
];
const DIMS_MOIS = [
  { c: 'marque', l: 'Marque' }, { c: 'modele', l: 'Modèle' }, { c: 'groupe', l: 'Groupe' },
  { c: 'client', l: 'Type de client' }, { c: 'origine', l: 'Origine' }, { c: 'constructeur', l: 'Constructeur' },
];

// Rang et son évolution vs N-1 : delta = rang N-1 − rang (positif = gagné).
function Rang({ r }) {
  if (!r || r.rang == null) return null;
  if (r.rang_n1 == null) return <span className="mk-rang mk-new" title="Absent du classement N-1">nouv.</span>;
  if (r.delta_rang === 0) return <span className="mk-rang" title={`Rang ${r.rang}, stable vs N-1`}>=</span>;
  return <span className={`mk-rang${cls(r.delta_rang)}`} title={`Rang ${r.rang_n1} → ${r.rang} vs N-1`}>{r.delta_rang > 0 ? '+' : ''}{r.delta_rang}</span>;
}

// Tableau à dépliage (jusqu'à 3 niveaux) : volume, évol. N-1, part, évol. en points.
function TableParts({ rows, vue, notre }) {
  const [ouverts, setOuverts] = useState(() => new Set());
  if (!rows || !rows.length) return <p className="mk-vide">Aucune immatriculation sur la période.</p>;
  const basc = (k) => setOuverts((o) => { const x = new Set(o); x.has(k) ? x.delete(k) : x.add(k); return x; });
  const total = rows.reduce((t, r) => t + r.volume, 0); const totalN1 = rows.reduce((t, r) => t + (r.volume_n1 || 0), 0);
  const lignes = [];
  const pousser = (r, niveau, cle) => {
    const enfants = vue.enfants[niveau] ? (r[vue.enfants[niveau]] || []) : [];
    const on = ouverts.has(cle);
    lignes.push(
      <tr key={cle} className={`mk-n${niveau}${enfants.length ? ' tbl-clic' : ''}${niveau === 0 && notre && r.label === notre ? ' tbl-courant' : ''}`}
        onClick={enfants.length ? () => basc(cle) : undefined}>
        {vue.rang && <td className="mk-num-rang">{niveau === 0 ? (r.rang ?? '') : ''}</td>}
        <td className="l" style={{ paddingLeft: 10 + niveau * 18 }}>
          <span className={`mk-chev${on ? ' on' : ''}${enfants.length ? '' : ' vide'}`}>▸</span>{r.label}
          {niveau === 0 && r.nous && <span className="mk-nous" title="Marque distribuée par notre groupe dans le département filtré">nous</span>}
          {niveau === 0 && vue.rang && <Rang r={r} />}
        </td>
        <td>{nb(r.volume)}</td>
        <td className={`vt-d${clsD(r.delta_volume)}`}>{signeNb(r.delta_volume)}</td>
        <td className="vt-taux">{pct1(r.part)}</td>
        <td className={`vt-d${clsD(r.delta_part)}`}>{signePt(r.delta_part)}</td>
      </tr>);
    if (on) enfants.forEach((e) => pousser(e, niveau + 1, `${cle}|${e.label}`));
  };
  rows.forEach((r) => pousser(r, 0, r.label));
  return (
    <table className="vt-tbl tbl-dense mk-tbl">
      <thead><tr>
        {vue.rang && <th>#</th>}
        <th className="l">{vue.col}</th><th>Volume</th><th className="vt-dh">vs N-1</th><th>Part de marché</th><th className="vt-dh">vs N-1</th>
      </tr></thead>
      <tbody>{lignes}</tbody>
      <tfoot><tr>
        {vue.rang && <td />}
        <td className="l">Total</td><td>{nb(total)}</td><td className={`vt-d${clsD(total - totalN1)}`}>{signeNb(total - totalN1)}</td><td>100 %</td><td />
      </tr></tfoot>
    </table>
  );
}

// Simulation par concession : le portefeuille de chaque concession (Référentiel
// Marché ▸ Affectations ▸ concession) appliqué à chaque département. Cellule
// en gras = département d'origine, part RÉELLE ; les autres sont ce que ferait
// le même portefeuille ailleurs. Couleur = écart à la part d'origine (vert :
// le portefeuille fait mieux ailleurs qu'à la maison, donc potentiel chez nous).
// ─── Rang d'un territoire (2026-09-30) ────────────────────────────────────
// Vignette à côté du taux de NOTRE territoire : son rang parmi tous les
// départements de la ligne, sur la PART de marché (même en affichage volumes :
// un volume dépend de la taille du marché, une part non). 1 = meilleure part,
// en vert ; dernier, en rouge ; ex æquo = même rang. Seuls comptent les
// départements où la marque (ou le portefeuille) est vendue.
function rangDe(cells, cell) {
  const parts = cells.filter((c) => c.part != null && c.volume).map((c) => c.part);
  if (cell.part == null || !cell.volume || parts.length < 2) return null;
  return { r: 1 + parts.filter((p) => p > cell.part).length, n: parts.length };
}
function RangTerritoire({ rang }) {
  if (!rang) return null;
  const ton = rang.r === 1 ? ' mk-rt-1' : rang.r === rang.n ? ' mk-rt-der' : '';
  return <span className={`mk-rt${ton}`} title={`Rang de ce territoire : ${rang.r} sur ${rang.n} départements, sur la part de marché`}>{rang.r}</span>;
}

// Volume + vignette d'évolution vs N-1, en volume (+120) ou en % (+4,2 %).
// Sans N-1, la vignette en % n'a pas de sens : elle est tue.
function CelluleEvol({ v, n1, mode, fort }) {
  const vol = v || 0, ref = n1 || 0;
  if (!vol && !ref) return <td className="mk-ev-c">·</td>;
  const d = vol - ref;
  const txt = mode === 'pct' ? (ref ? `${d > 0 ? '+' : ''}${pct1((d / ref) * 100)}` : null) : signeNb(d);
  return (
    <td className="mk-ev-c" title={`${nb(vol)} immat. · N-1 : ${nb(ref)}`}>
      {fort ? <b>{nb(vol)}</b> : nb(vol)}
      {txt != null && <span className={`mk-ev${d > 0 ? ' tbl-pos' : d < 0 ? ' tbl-neg' : ''}`}>{txt}</span>}
    </td>
  );
}

function Simulation({ lignes, departements, mode }) {
  if (!lignes || !lignes.length) return <p className="mk-vide">Aucune concession renseignée dans le Référentiel Marché.</p>;
  return (
    <table className="vt-tbl tbl-dense mk-tbl">
      <thead><tr><th className="l">Concession</th>{departements.map((d) => <th key={d}>{capDep(d)}</th>)}</tr></thead>
      <tbody>
        {lignes.map((l) => {
          const ref = l.cells.find((c) => c.origine);
          return (
            <tr key={l.concession}>
              <td className="l"><b>{l.concession}</b><div className="mk-portef">{l.marques.join(' · ')}</div></td>
              {l.cells.map((c) => {
                const ecart = !c.origine && ref && c.part != null && ref.part != null ? c.part - ref.part : null;
                return (
                  <td key={c.departement} className={`${c.origine ? 'mk-origine' : ''}${mode === 'part' && ecart ? (ecart > 0 ? ' tbl-pos' : ' tbl-neg') : ''}`}
                    title={`${capDep(c.departement)} · ${nb(c.volume)} immat. · ${pct1(c.part)} de part${c.origine ? ' (réel)' : ' (simulé)'} · ${signePt(c.delta_part)} vs N-1`}>
                    {mode === 'volume' ? nb(c.volume) : pct1(c.part)}
                    {c.origine && <RangTerritoire rang={rangDe(l.cells, c)} />}
                    <div className={`mk-sous${clsD(c.delta_part) ? ` ${c.delta_part > 0 ? 'tbl-pos' : 'tbl-neg'}` : ''}`}>{mode === 'volume' ? (c.origine ? 'réel' : 'simulé') : `${signePt(c.delta_part)} vs N-1`}</div>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// Nos marques × département : parts ou volumes, drill modèle ▸ type de client.
// « Nos marques » vient du Référentiel Marché : marques affectées au groupe
// AUBERY dans le département. Une cellule où la marque est distribuée par un
// autre groupe est grisée ; meilleur / moins bon territoire comparés entre nos
// seuls départements.
function NosMarques({ data, mode }) {
  const [ouverts, setOuverts] = useState(() => new Set());
  if (!data || !data.rows.length) return <p className="mk-vide">Aucune marque affectée au groupe {data?.groupe} dans le référentiel.</p>;
  const basc = (k) => setOuverts((o) => { const x = new Set(o); x.has(k) ? x.delete(k) : x.add(k); return x; });
  const val = (c) => (mode === 'volume' ? c.volume : c.part);
  const cellules = (cells, k) => {
    const nos = cells.filter((c) => c.nous && val(c) != null).map(val);
    const max = nos.length > 1 ? Math.max(...nos) : null; const min = nos.length > 1 ? Math.min(...nos) : null;
    return cells.map((c) => {
      const v = val(c);
      const extr = c.nous && max !== min ? (v === max ? ' tbl-pos mk-fort' : v === min ? ' tbl-neg mk-fort' : '') : '';
      return <td key={k + c.departement} className={`${c.nous ? '' : 'mk-autre'}${extr}`}
        title={`${capDep(c.departement)} · ${nb(c.volume)} immat.${c.nous ? '' : ' · distribuée par un autre groupe'}`}>
        {v == null || (!c.volume && mode === 'part') ? '·' : mode === 'volume' ? nb(v) : pct1(v)}
        {c.nous && <RangTerritoire rang={rangDe(cells, c)} />}</td>;
    });
  };
  const lignes = [];
  data.rows.forEach((r) => {
    const k = r.marque; const on = ouverts.has(k);
    lignes.push(<tr key={k} className="mk-n0 tbl-clic" onClick={() => basc(k)}>
      <td className="l"><span className={`mk-chev${on ? ' on' : ''}`}>▸</span>{r.marque}</td>{cellules(r.cells, k)}</tr>);
    if (on) r.modeles.forEach((m) => {
      const km = `${k}|${m.label}`; const onm = ouverts.has(km);
      lignes.push(<tr key={km} className="mk-n1 tbl-clic" onClick={() => basc(km)}>
        <td className="l" style={{ paddingLeft: 28 }}><span className={`mk-chev${onm ? ' on' : ''}${m.clients.length ? '' : ' vide'}`}>▸</span>{m.label}</td>{cellules(m.cells, km)}</tr>);
      if (onm) m.clients.forEach((cl) => lignes.push(<tr key={`${km}|${cl.label}`} className="mk-n2">
        <td className="l" style={{ paddingLeft: 46 }}>{cl.label}</td>{cellules(cl.cells, `${km}|${cl.label}`)}</tr>));
    });
  });
  return (
    <table className="vt-tbl tbl-dense mk-tbl">
      <thead><tr><th className="l">Marque</th>{data.departements.map((d) => <th key={d}>{capDep(d)}</th>)}</tr></thead>
      <tbody>{lignes}</tbody>
    </table>
  );
}

// Panneau de filtres dédié au marché (pas de sociétés : données marché).
export function PanneauMarche({ ouvert, onToggle, periode, periodes = PERIODES, onPeriode, moisActifs, onToggleMois, depts, deptsDispo, onToggleDept, clients = [], clientsDispo = [], onToggleClient, onReset, nbActifs }) {
  if (!ouvert) {
    return (
      <aside className="fp-rail">
        <button className="fp-rail-bouton" onClick={onToggle} title="Ouvrir les filtres">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
          {nbActifs > 0 && <span className="fp-badge">{nbActifs}</span>}
        </button>
      </aside>
    );
  }
  return (
    <aside className="fp-panneau" aria-label="Filtres">
      <div className="fp-tete"><span className="fp-titre">Filtres{nbActifs > 0 && <span className="fp-badge">{nbActifs}</span>}</span>
        <button className="fp-replier" onClick={onToggle}>»</button></div>
      <div className="fp-section">
        <div className="fp-section-titre">Période</div>
        {periodes.map((p) => (
          <label key={p.code} className="fp-ligne"><input type="radio" name="mk-per" checked={periode === p.code} onChange={() => onPeriode(p.code)} /><span>{p.label}</span></label>
        ))}
        <div className="fp-sous-titre">Ou mois précis (cumulables)</div>
        <div className="fp-mois-liste">
          {MOIS_DISPO.map((m) => (
            <label key={m.code} className={`fp-ligne${m.desactive ? ' fp-off' : ''}`}><input type="checkbox" disabled={!!m.desactive} checked={moisActifs.includes(m.code)} onChange={() => onToggleMois(m.code)} /><span>{m.label}</span></label>
          ))}
        </div>
      </div>
      <div className="fp-section">
        <div className="fp-section-titre">Département</div>
        {deptsDispo.map((d) => (
          <label key={d} className="fp-ligne"><input type="checkbox" checked={depts.includes(d)} onChange={() => onToggleDept(d)} /><span>{d.charAt(0) + d.slice(1).toLowerCase()}</span></label>
        ))}
      </div>
      {/* Type de client (2026-09-30) : restreint tout le rapport, marché
          compris — les parts deviennent des parts du marché retenu. */}
      {clientsDispo.length > 0 && onToggleClient && (
        <div className="fp-section">
          <div className="fp-section-titre">Type de client</div>
          {clientsDispo.map((c) => (
            <label key={c} className="fp-ligne"><input type="checkbox" checked={clients.includes(c)} onChange={() => onToggleClient(c)} /><span>{c}</span></label>
          ))}
        </div>
      )}
      <button className="fp-reset" onClick={onReset} disabled={nbActifs === 0}>Réinitialiser les filtres</button>
    </aside>
  );
}

export default function Marche() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, toggle] = usePanneau();
  const [vueParts, setVueParts] = useState('marque');
  const [dimEvo, setDimEvo] = useState('marque');
  const [mesureEvo, setMesureEvo] = useState('part');
  const [mesureNos, setMesureNos] = useState('part');
  const [vueNos, setVueNos] = useState('marque');
  const [evolDep, setEvolDep] = useState('volume');   // vignettes vs N-1 de la vue par département
  const [ia, setIa] = useState(false);
  const { ia: droitIa } = useBi(); // droit « Analyse IA » de la personne connectée

  const periode = PERIODES.some((x) => x.code === params.get('periode')) ? params.get('periode') : 'ytd';
  const moisStr = params.get('mois') || '';
  const moisActifs = moisStr ? moisStr.split(',').filter(Boolean) : [];
  const vpvu = params.get('vpvu') || '';
  const depts = (params.get('depts') || '').split(',').filter(Boolean);
  const clients = (params.get('clients') || '').split('|').filter(Boolean);

  const setFiltre = (cles) => { const next = new URLSearchParams(params); Object.entries(cles).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k))); setParams(next, { replace: true }); };
  const toggleDept = (d) => { const s = new Set(depts); s.has(d) ? s.delete(d) : s.add(d); const l = [...s]; setFiltre({ depts: l.join(',') }); };
  const toggleMois = (c) => { const s = new Set(moisActifs); s.has(c) ? s.delete(c) : s.add(c); setFiltre({ mois: [...s].sort().join(',') }); };
  const toggleClient = (c) => { const s = new Set(clients); s.has(c) ? s.delete(c) : s.add(c); setFiltre({ clients: [...s].join('|') }); };
  const moisModeActif = moisActifs.length > 0;
  const nbActifs = ((moisModeActif || periode !== 'ytd') ? 1 : 0) + (vpvu ? 1 : 0) + (depts.length ? 1 : 0) + (clients.length ? 1 : 0);

  useEffect(() => {
    let actif = true; setErreur('');
    api.charger({ periode, mois: moisStr, vpvu, depts: depts.join(','), clients: clients.join('|') }).then((r) => { if (actif) setData(r.data); }).catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode, moisStr, vpvu, params.get('depts'), params.get('clients')]);

  const Panneau = (
    <PanneauMarche ouvert={ouvert} onToggle={toggle} periode={moisModeActif ? null : periode}
      onPeriode={(c) => setFiltre({ periode: c === 'ytd' ? '' : c, mois: '' })}
      moisActifs={moisActifs} onToggleMois={toggleMois}
      depts={depts} deptsDispo={data?.departementsDispo || ['GUADELOUPE', 'MARTINIQUE', 'GUYANE', 'REUNION', 'MAYOTTE']}
      onToggleDept={toggleDept} clients={clients} clientsDispo={data?.clientsDispo || []} onToggleClient={toggleClient}
      nbActifs={nbActifs} onReset={() => setFiltre({ periode: '', mois: '', vpvu: '', depts: '', clients: '' })} />
  );

  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  const g = data.kpis_globaux;
  const vue = VUES_PARTS.find((v) => v.c === vueParts);
  const evo = data.evolution;
  const dimMois = evo.dims[dimEvo] || { entities: [], values: {} };
  const celluleMois = (ent, m) => {
    const v = (dimMois.values[m] || {})[ent] || 0; const t = evo.volume_total[m] || 0;
    return mesureEvo === 'volume' ? (v ? nb(v) : '·') : (t && v ? pct1(Math.round((v / t) * 1000) / 10) : '·');
  };
  const Kpi = ({ l, v, d, dcls, sous }) => (
    <div className="vt-kpi"><div className="l">{l}</div><div className="v">{v}</div>
      <div className={`d${dcls ? ` ${dcls}` : ''}`}>{d}{sous ? <small> {sous}</small> : null}</div></div>
  );
  const posneg = (x) => (x == null ? '' : x >= 0 ? 'pos' : 'neg');

  return (
    <>
      <main className="vt-page">
        <div className="vt-head">
          <div><h1>Marché — Immatriculations</h1></div>
          <div className="mk-tete-d">
            {droitIa !== 'aucune' && <button type="button" className="mk-ia" onClick={() => setIa(true)} title="Analyse IA du marché : Martinique et Guadeloupe, AUBERY et ses concurrents, recommandations">Analyse IA</button>}
            <div className="vt-asof"><b>{data.periode}</b>{data.arrete && <><br /><span className={data.arrete.partiel ? 'mk-arrete partiel' : 'mk-arrete'}>données au {dateArrete(data.arrete.date)}{data.arrete.partiel ? ` · ${data.arrete.joursEcoules}/${data.arrete.joursMois} jours ouvrés, N-1 sur le mois complet` : ''}</span></>}<br />{depts.length ? depts.map(capDep).join(', ') : 'tous départements'}{vpvu ? ` · ${vpvu}` : ' · VP + VUL'}{clients.length ? ` · ${clients.length === 1 ? clients[0] : `${clients.length} types de client`}` : ''}</div>
          </div>
        </div>
        {ia && <MarcheAnalyseIa filtres={{ periode, mois: moisStr, vpvu }} libellePerimetre={`${data.periode}${vpvu ? ` · ${vpvu}` : ' · VP + VUL'}`} onFermer={() => setIa(false)} />}

        <div className="vt-ctl">
          <span><span className="lab">Catégorie</span>
            <span className="vt-seg">{VPVU.map((v) => <button key={v.code} className={vpvu === v.code ? 'on' : ''} onClick={() => setFiltre({ vpvu: v.code })}>{v.label}</button>)}</span>
          </span>
        </div>

        <div className="vt-kpis" style={{ gridTemplateColumns: 'repeat(6,1fr)' }}>
          <Kpi l="Volume marché" v={nb(g.volume_total)} d={g.delta_volume_pct == null ? '—' : `${g.delta_volume_pct > 0 ? '+' : ''}${pct1(g.delta_volume_pct)}`} dcls={posneg(g.delta_volume_pct)} sous={`vs N-1 (${nb(g.volume_total_n1)})`} />
          <Kpi l="Marque leader" v={g.marque_leader ? g.marque_leader.label : '—'} d={g.marque_leader ? signePt(g.marque_leader.delta_part) : '—'} dcls={posneg(g.marque_leader?.delta_part)} sous={g.marque_leader ? `· ${pct1(g.marque_leader.part)} de part` : ''} />
          <Kpi l="Groupe leader" v={g.groupe_leader ? g.groupe_leader.label : '—'} d={g.groupe_leader ? signePt(g.groupe_leader.delta_part) : '—'} dcls={posneg(g.groupe_leader?.delta_part)} sous={g.groupe_leader ? `· ${pct1(g.groupe_leader.part)} de part` : ''} />
          <Kpi l={`Notre groupe (${g.notre_groupe.label})`} v={pct1(g.notre_groupe.part)} d={signePt(g.notre_groupe.delta_part)} dcls={posneg(g.notre_groupe.delta_part)} sous={`· ${nb(g.notre_groupe.volume)} immat.${g.notre_groupe.rang ? ` · ${g.notre_groupe.rang}e groupe` : ''}`} />
          <Kpi l="Marques" v={nb(g.nb_marques)} d="présentes" />
          <Kpi l="Groupes" v={nb(g.nb_groupes)} d="rattachés" sous={g.non_rattache ? `· ${pct1(g.non_rattache)} du marché non rattaché` : ''} />
        </div>

        {/* ── Vue par département ── */}
        {/* 2026-10-01 : la colonne « part » (poids du département dans le
            marché) est retirée ; chaque volume — total puis par type de
            client — porte son évolution vs N-1 en vignette, en volume ou en %
            selon le sélecteur (réglage d'affichage ⇒ en-tête de carte). */}
        <div className="vt-card">
          <div className="vt-card-h"><h2>Vue par département</h2>
            <div className="st-tools">
              <span className="st-seg">
                <button type="button" className={evolDep === 'volume' ? 'on' : ''} onClick={() => setEvolDep('volume')}>vs N-1 en volume</button>
                <button type="button" className={evolDep === 'pct' ? 'on' : ''} onClick={() => setEvolDep('pct')}>vs N-1 en %</button>
              </span>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className="vt-tbl tbl-dense mk-dep">
              <thead><tr><th className="l">Département</th><th>Volume</th>
                {(data.types_client || []).map((c) => <th key={c}>{c}</th>)}
                <th className="l">Marque leader</th><th>Part de la marque leader</th></tr></thead>
              <tbody>
                {data.par_departement.map((d) => (
                  <tr key={d.label} className={`tbl-clic${depts.length === 1 && depts[0] === d.label ? ' actif' : ''}`}
                    onClick={() => setFiltre({ depts: basculer(depts.join(','), d.label) })}>
                    <td className="l">{capDep(d.label)}</td>
                    <CelluleEvol v={d.volume} n1={d.volume_n1} mode={evolDep} fort />
                    {(data.types_client || []).map((c) => <CelluleEvol key={c} v={d.clients?.[c]?.volume} n1={d.clients?.[c]?.volume_n1} mode={evolDep} />)}
                    <td className="l">{d.marque_leader ? d.marque_leader.marque : '—'}</td>
                    <td>{d.marque_leader ? pct1(d.marque_leader.part) : '—'}</td>
                  </tr>
                ))}
              </tbody>
              {data.total_departement && data.par_departement.length > 1 && (
                <tfoot><tr>
                  <td className="l">Total</td>
                  <CelluleEvol v={data.total_departement.volume} n1={data.total_departement.volume_n1} mode={evolDep} />
                  {(data.types_client || []).map((c) => <CelluleEvol key={c} v={data.total_departement.clients?.[c]?.volume} n1={data.total_departement.clients?.[c]?.volume_n1} mode={evolDep} />)}
                  <td className="l">{data.total_departement.marque_leader ? data.total_departement.marque_leader.marque : '—'}</td>
                  <td>{data.total_departement.marque_leader ? pct1(data.total_departement.marque_leader.part) : '—'}</td>
                </tr></tfoot>
              )}
            </table>
          </div>
        </div>

        {/* ── Parts de marché : 6 vues à dépliage ── */}
        <div className="vt-card">
          <div className="vt-card-h"><h2>Parts de marché</h2>
            <div className="st-tools">
              <span className="hint">rang vs N-1</span>
              <span className="st-seg">{VUES_PARTS.map((v) => <button key={v.c} type="button" className={vueParts === v.c ? 'on' : ''} onClick={() => setVueParts(v.c)}>{v.l}</button>)}</span>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <TableParts key={vueParts} rows={data[vue.cle]} vue={vue} notre={vue.c === 'groupe' ? g.notre_groupe.label : null} />
            {vue.c === 'modele' && data.par_modele_total > data.par_modele.length && (
              <p className="mk-vide">{data.par_modele.length} premiers modèles affichés sur {nb(data.par_modele_total)}.</p>
            )}
          </div>
        </div>

        {/* ── Évolution mensuelle ── */}
        <div className="vt-card">
          <div className="vt-card-h"><h2>Évolution mensuelle</h2>
            <div className="st-tools">
              <span className="st-seg">{DIMS_MOIS.map((d) => <button key={d.c} type="button" className={dimEvo === d.c ? 'on' : ''} onClick={() => setDimEvo(d.c)}>{d.l}</button>)}</span>
              <span className="st-seg">
                <button type="button" className={mesureEvo === 'part' ? 'on' : ''} onClick={() => setMesureEvo('part')}>Parts %</button>
                <button type="button" className={mesureEvo === 'volume' ? 'on' : ''} onClick={() => setMesureEvo('volume')}>Volumes</button>
              </span>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            {!evo.mois.length ? <p className="mk-vide">Aucune immatriculation sur la période.</p> : (
              <table className="vt-tbl tbl-dense mk-tbl">
                <thead><tr><th className="l">{DIMS_MOIS.find((d) => d.c === dimEvo).l}</th>{evo.mois.map((m) => <th key={m}>{moisAn(m)}</th>)}</tr></thead>
                <tbody>
                  {dimMois.entities.map((e) => (
                    <tr key={e} className={dimEvo === 'groupe' && e === g.notre_groupe.label ? 'tbl-courant' : undefined}>
                      <td className="l">{e}</td>{evo.mois.map((m) => <td key={m}>{celluleMois(e, m)}</td>)}
                    </tr>
                  ))}
                </tbody>
                <tfoot><tr><td className="l">Total marché</td>{evo.mois.map((m) => <td key={m}>{mesureEvo === 'volume' ? nb(evo.volume_total[m]) : '100 %'}</td>)}</tr></tfoot>
              </table>
            )}
          </div>
        </div>

        {/* ── Nos marques × département ── */}
        <div className="vt-card">
          <div className="vt-card-h"><h2>Nos marques</h2>
            <div className="st-tools">
              {vueNos === 'simulation' && <span className="hint">portefeuille de chaque concession appliqué à chaque département · gras = réel · vert / rouge = mieux / moins bien qu'à la maison · vignette = rang du département d'origine parmi tous (part de marché)</span>}
              <span className="st-seg">
                <button type="button" className={vueNos === 'marque' ? 'on' : ''} onClick={() => setVueNos('marque')}>Par marque</button>
                <button type="button" className={vueNos === 'simulation' ? 'on' : ''} onClick={() => setVueNos('simulation')}>Simulation par concession</button>
              </span>
              <span className="st-seg">
                <button type="button" className={mesureNos === 'part' ? 'on' : ''} onClick={() => setMesureNos('part')}>Parts %</button>
                <button type="button" className={mesureNos === 'volume' ? 'on' : ''} onClick={() => setMesureNos('volume')}>Volumes</button>
              </span>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            {vueNos === 'marque'
              ? <NosMarques data={data.nos_marques} mode={mesureNos} />
              : <Simulation lignes={data.nos_marques.simulation} departements={data.nos_marques.departements} mode={mesureNos} />}
          </div>
        </div>
      </main>
      {Panneau}
    </>
  );
}
